import React from 'react';
import { Box, Card, CardContent, Tooltip, Typography } from '@mui/material';
import { DashboardDensity, isCompact } from '../../utils/dashboardDensity';

interface KpiStatCardProps {
  label: string;
  value: number | string;
  color?: string;
  tooltip?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  density?: DashboardDensity;
  active?: boolean;
  onClick?: () => void;
}

const KpiStatCard: React.FC<KpiStatCardProps> = ({
  label,
  value,
  color,
  tooltip,
  subtitle,
  icon,
  density = 'standard',
  active = false,
  onClick,
}) => {
  const compact = isCompact(density);
  const height = compact ? 64 : 76;
  const accent = color || '#1976d2';
  const clickable = Boolean(onClick);

  const card = (
    <Card
      onClick={onClick}
      sx={{
        height,
        borderLeft: `3px solid ${accent}`,
        boxShadow: active ? 2 : 'none',
        border: '1px solid',
        borderColor: active ? accent : 'divider',
        borderLeftWidth: 3,
        borderLeftColor: accent,
        bgcolor: active ? `${accent}18` : 'background.paper',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'background-color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
        '&:hover': clickable ? { bgcolor: active ? `${accent}22` : `${accent}0c` } : undefined,
      }}
    >
      <CardContent
        sx={{
          p: compact ? '6px 10px !important' : '8px 12px !important',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: compact ? 0.75 : 1,
          '&:last-child': { pb: compact ? '6px !important' : '8px !important' },
        }}
      >
        {icon && (
          <Box sx={{ color: accent, display: 'flex', alignItems: 'center', '& svg': { fontSize: compact ? 18 : 22 } }}>
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: compact ? '0.65rem' : '0.7rem', lineHeight: 1.15, display: 'block', whiteSpace: 'normal' }}
          >
            {label}
          </Typography>
          <Typography
            fontWeight={700}
            noWrap
            sx={{ fontSize: compact ? '1.1rem' : '1.35rem', lineHeight: 1.2 }}
          >
            {value}
          </Typography>
          {!compact && subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.65rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const tip = [
    tooltip,
    compact && subtitle ? subtitle : null,
    onClick ? 'Click to filter the employee list below. Click again to clear.' : null,
  ].filter(Boolean).join('\n');
  if (!tip) return card;

  return (
    <Tooltip title={<Box component="span" sx={{ whiteSpace: 'pre-line' }}>{tip}</Box>} arrow placement="top">
      <Box sx={{ height: '100%', cursor: onClick ? 'pointer' : 'help' }}>{card}</Box>
    </Tooltip>
  );
};

export default KpiStatCard;
